import { AxiosRequestConfig } from 'axios';
import { SsoType } from '@diritto/common/account/constants/sso.constant';
import {
  AuthSsoAccessTokenRequestDto,
  SsoLinkRequestDto,
  SsoLinkResponseDto,
} from '@diritto/common/account/dtos/auth.dto';
import { CertificateRequestDto, CertificateResponseDto } from '@diritto/common/account/dtos/certificate.dtos';
import { CreateUserMarketingAgreementDto } from '@diritto/common/account/dtos/create-user-marketing-agreement.dto';
import { RevertDormantRequestDto } from '@diritto/common/account/dtos/dormant-user.dto';
import { RefreshTokenResponseDto } from '@diritto/common/account/dtos/refresh-token.response.dto';
import { ReplaceTokenDto } from '@diritto/common/account/dtos/replace-token.dto';
import { ReplaceTokenResponseDto } from '@diritto/common/account/dtos/replace-token.response.dto';
import { SignInDto } from '@diritto/common/account/dtos/sign-in.dto';
import { SignInResponseDto } from '@diritto/common/account/dtos/sign-in.response.dto';
import { SignUpRequestDto } from '@diritto/common/account/dtos/sign-up-request.dto';
import { SignUpResponseDto } from '@diritto/common/account/dtos/sign-up-response.dto';
import { UserMarketingAgreementResponseDto } from '@diritto/common/account/dtos/user-marketing-agreement.response.dto';
import { UserMeResponseDto } from '@diritto/common/account/dtos/user-me.response.dto';
import { ResponseDto } from '@diritto/common/base/response.dto';
import { Http } from '@diritto/common/utils/requester/http';
import { buildUrl } from '@diritto/common/utils/urls';
import { UserZendeskJwtResponseDto } from '@diritto/common/account/dtos/user.dtos';
import { EmailVerificationResponseDto } from '@diritto/common/account/dtos/email-verification-response';
import { EmailVerificationRequestDto } from '@diritto/common/account/dtos/email-verification-request.dto';
import { EmailVerificationVerifyRequestDto } from '@diritto/common/account/dtos/email-verification-verify-request.dto';
import { CreatePartnerAccountRequestDto } from '@diritto/common/account/dtos/create-partner-account-request.dto';

export class AccountApi {
  constructor(
    protected url: string,
    protected http: Http,
  ) {}

  async me(options?: AxiosRequestConfig): Promise<UserMeResponseDto> {
    const res = await this.http.get<UserMeResponseDto>(`${this.url}/accounts/me`, options);
    return res.data;
  }

  async signWithSsoAccessToken(ssoType: SsoType, params: AuthSsoAccessTokenRequestDto) {
    return this.http.post<AuthSsoAccessTokenRequestDto, SignInResponseDto>(
      `${this.url}/accounts/auth/sso/${ssoType}/access-token`,
      params,
    );
  }

  async signInEmail(params: SignInDto) {
    return this.http.post<SignInDto, SignInResponseDto>(`${this.url}/accounts/auth/email/sign-in`, params);
  }

  async signUp(params: SignUpRequestDto, normalizedReturnUrl?: string) {
    return this.http.post<SignUpRequestDto, SignUpResponseDto>(
      buildUrl(`${this.url}/accounts/auth/sign-up`, { returnUrl: normalizedReturnUrl }),
      params,
    );
  }

  async signOut(): Promise<ResponseDto> {
    const res = await this.http.get<ResponseDto>(`${this.url}/accounts/auth/sign-out`, { withCredentials: true });
    return res.data;
  }

  async refreshToken(options?: AxiosRequestConfig) {
    return this.http.post<void, RefreshTokenResponseDto>(`${this.url}/accounts/auth/refresh-token`, undefined, options);
  }

  async replaceToken(params: ReplaceTokenDto) {
    return this.http.post<ReplaceTokenDto, ReplaceTokenResponseDto>(`${this.url}/accounts/auth/replace-token`, params);
  }

  async getUserMarketingAgreement(options?: AxiosRequestConfig) {
    const res = await this.http.get<UserMarketingAgreementResponseDto>(
      `${this.url}/accounts/user-marketing-agreement`,
      options,
    );
    return res.data;
  }

  async setUserMarketingAgreement(params: CreateUserMarketingAgreementDto) {
    const res = await this.http.post<CreateUserMarketingAgreementDto, UserMarketingAgreementResponseDto>(
      `${this.url}/accounts/user-marketing-agreement`,
      params,
    );
    return res.data;
  }

  async getCertificationInfo(params: CertificateRequestDto) {
    const res = await this.http.post<CertificateRequestDto, CertificateResponseDto>(
      `${this.url}/accounts/certification`,
      params,
    );
    return res.data;
  }

  async updateCertificationInfo(params: CertificateRequestDto) {
    const res = await this.http.put<CertificateRequestDto, CertificateResponseDto>(
      `${this.url}/accounts/certification`,
      params,
    );
    return res.data;
  }

  async withdraw() {
    const res = await this.http.delete<ResponseDto>(`${this.url}/accounts`);
    return res.data;
  }

  async revertDormant(parmas: RevertDormantRequestDto) {
    const res = await this.http.post<RevertDormantRequestDto, ResponseDto>(
      `${this.url}/accounts/revert-dormant`,
      parmas,
    );
    return res.data;
  }

  async linkSso(ssoType: SsoType, data: AuthSsoAccessTokenRequestDto) {
    const res = await this.http.post<AuthSsoAccessTokenRequestDto, SsoLinkResponseDto>(
      `${this.url}/accounts/sso/${ssoType}/link`,
      data,
    );
    return res.data;
  }

  async linkSsoAtSign(data: SsoLinkRequestDto) {
    const res = await this.http.post<SsoLinkRequestDto, ResponseDto>(`${this.url}/accounts/sso/link`, data);
    return res.data;
  }

  async unlinkSso(ssoType: SsoType) {
    const res = await this.http.delete<ResponseDto>(`${this.url}/accounts/sso/${ssoType}/unlink`);
    return res.data;
  }

  async getZendeskToken() {
    const res = await this.http.post<void, UserZendeskJwtResponseDto>(`${this.url}/accounts/zendesk-token`);
    return res.data;
  }

  async sendSignUpVerificationToken(data: EmailVerificationRequestDto) {
    const res = await this.http.post<EmailVerificationRequestDto, EmailVerificationResponseDto>(
      `${this.url}/accounts/auth/email/send-sign-up-verification-token`,
      data,
    );
    return res.data;
  }

  async sendSignInVerificationToken(data: EmailVerificationRequestDto) {
    const res = await this.http.post<EmailVerificationRequestDto, EmailVerificationResponseDto>(
      `${this.url}/accounts/auth/email/send-sign-in-verification-token`,
      data,
    );
    return res.data;
  }

  async createPartnerAccount(data: CreatePartnerAccountRequestDto) {
    const res = await this.http.post<CreatePartnerAccountRequestDto, SignInResponseDto>(
      `${this.url}/accounts/auth/sign-up/partner`,
      data,
    );
    return res.data;
  }

  async verifyEmailVerificationToken(data: EmailVerificationVerifyRequestDto) {
    const res = await this.http.post<EmailVerificationVerifyRequestDto, SignInResponseDto>(
      `${this.url}/accounts/auth/email/verify`,
      data,
    );
    return res.data;
  }

  async verifySignInVerificationTokenAndSignIn(data: EmailVerificationVerifyRequestDto) {
    const res = await this.http.post<EmailVerificationVerifyRequestDto, SignInResponseDto>(
      `${this.url}/accounts/auth/email/verify-sign-in`,
      data,
    );
    return res.data;
  }
}
